import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { useApi } from '@fortem-monorepo/react-spa'
import { MarketSummary } from '@fortem-monorepo/web-sdk'

import { toastApiError } from '@/lib/error-toast'

export const useEmailMarketSummaryMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: ({ display_id, ...params }: MarketSummary.ExportReportEmailParams & { display_id: number }) =>
      api.marketSummary.display(display_id).exportAsEmail(params),
    retry: false,
    onSuccess: () => toast.success('Market summary emailed to you'),
    onError: (error) => toastApiError(error, 'Error emailing market summary'),
  })
}

const useInvalidateMarketSummary = () => {
  const queryClient = useQueryClient()

  return () => queryClient.invalidateQueries({ queryKey: ['market-summary'] })
}

export const useAddInstrumentToDisplayMutation = (display_id: number) => {
  const api = useApi()
  const invalidate = useInvalidateMarketSummary()

  return useMutation({
    mutationFn: (params: MarketSummary.AddInstrumentToDisplayParams) => {
      return api.marketSummary.display(display_id).instruments.add(params)
    },
    onSuccess: async () => {
      toast.success('Instrument added')
      await invalidate()
    },
    onError: (error) => toastApiError(error, 'Error adding instrument'),
  })
}

export const useAddInstrumentMutation = () => {
  const api = useApi()
  const invalidate = useInvalidateMarketSummary()
  return useMutation({
    mutationFn: (params: MarketSummary.AddInstrumentParams) => api.marketSummary.addInstrument(params),
    onSuccess: async () => {
      toast.success('Instrument registered')
      await invalidate()
    },
    onError: (error) => toastApiError(error, 'Error registering instrument'),
  })
}

export const useRemoveInstrumentFromDisplayMutation = (display_id: number) => {
  const api = useApi()
  const invalidate = useInvalidateMarketSummary()

  return useMutation({
    mutationFn: (params: MarketSummary.RemoveInstrumentFromDisplayParams) => {
      return api.marketSummary.display(display_id).instruments.remove(params)
    },
    onSuccess: async () => {
      toast.success('Instrument removed')
      await invalidate()
    },
    onError: (error) => toastApiError(error, 'Error removing instrument'),
  })
}

export const useCreateEventMutation = () => {
  const api = useApi()
  const invalidate = useInvalidateMarketSummary()

  return useMutation({
    mutationFn: (params: MarketSummary.CreateEventParams) => {
      return api.marketSummary.createEvent(params)
    },
    onSuccess: async () => {
      toast.success('Event created')
      await invalidate()
    },
    onError: (error) => toastApiError(error, 'Error creating event'),
  })
}

export const useUpdateInstrumentMutation = () => {
  const api = useApi()
  const invalidate = useInvalidateMarketSummary()

  return useMutation({
    mutationFn: (params: MarketSummary.UpdateInstrumentParams) => api.marketSummary.updateInstrument(params),
    onSuccess: async () => {
      toast.success('Instrument updated')
      await invalidate()
    },
    onError: (error) => toastApiError(error, 'Error updating instrument'),
  })
}

export const useRemoveInstrumentMutation = () => {
  const api = useApi()
  const invalidate = useInvalidateMarketSummary()

  return useMutation({
    mutationFn: (params: MarketSummary.RemoveInstrumentFromDisplayParams) => api.marketSummary.removeInstrument(params),
    onSuccess: async () => {
      toast.success('Instrument removed')
      await invalidate()
    },
    onError: (error) => toastApiError(error, 'Error removing instrument'),
  })
}

export const useDeleteEventMutation = () => {
  const api = useApi()
  const invalidate = useInvalidateMarketSummary()

  return useMutation({
    mutationFn: (params: MarketSummary.DeleteEventParams) => {
      return api.marketSummary.deleteEvent(params)
    },
    onSuccess: async () => {
      toast.success('Event deleted')
      await invalidate()
    },
    onError: (error) => toastApiError(error, 'Error deleting event'),
  })
}

export const useAddMetricToDisplayMutation = () => {
  const api = useApi()
  const invalidate = useInvalidateMarketSummary()

  return useMutation({
    mutationFn: (params: MarketSummary.AddMetricToDisplayParams) => {
      return api.marketSummary.display(params.display_id).metrics.add(params)
    },
    onSuccess: async () => {
      toast.success('Metric added')
      await invalidate()
    },
    onError: (error) => toastApiError(error, 'Error adding metric'),
  })
}

export const useRemoveMetricFromDisplayMutation = () => {
  const api = useApi()
  const invalidate = useInvalidateMarketSummary()

  return useMutation({
    mutationFn: (params: MarketSummary.RemoveMetricFromDisplayParams & { display_id: number }) => {
      return api.marketSummary.display(params.display_id).metrics.remove(params)
    },
    onSuccess: async () => {
      toast.success('Metric removed')
      await invalidate()
    },
    onError: (error) => toastApiError(error, 'Error removing metric'),
  })
}

export const useUpdateMetricsDisplayOrderMutation = () => {
  const api = useApi()
  const invalidate = useInvalidateMarketSummary()

  return useMutation({
    mutationFn: (params: MarketSummary.UpdateMetricsDisplayOrderParams & { display_id: number }) => {
      return api.marketSummary.display(params.display_id).metrics.updateDisplayOrder(params)
    },
    onSuccess: async () => {
      toast.success('Display order updated')
      await invalidate()
    },
    onError: (error) => toastApiError(error, 'Error updating display order'),
  })
}
